import { getRandomInt } from "../Shared/Helpers.tsx";
import {
    BoxPlotQuestionOptions,
    BoxPlotSpec,
    ProblemData,
    ProblemFormatOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { boxPlotContexts as enContexts, boxPlotPrompts as en } from "../Files/Prompts/en/boxPlots.ts";
import { boxPlotContexts as esContexts, boxPlotPrompts as es } from "../Files/Prompts/es/boxPlots.ts";
import { boxPlotContexts as frContexts, boxPlotPrompts as fr } from "../Files/Prompts/fr/boxPlots.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const CONTEXTS: Record<PrintedLanguage, typeof enContexts> = { en: enContexts, es: esContexts, fr: frContexts };

/**
 * Box plots (ADR 0099 §3), read and never made. A box plot is drawn over a
 * number line whose every tick is labelled; whiskers run from the minimum to
 * the maximum, the box from the lower quartile to the upper, and a line across
 * the box marks the median. One plot is asked several questions: every
 * question in a group carries the same `problemGroup.boxPlot`, which
 * `PdfGenerator/Page.tsx` draws once above the group's rows.
 *
 * The rule this file exists to enforce is ADR 0022 §1a's: the sheet asks only
 * what the drawing shows exactly. Quartile conventions disagree when a student
 * computes quartiles from a list; they never disagree about a plot that is
 * already drawn. So nothing here makes a plot from a list: the five numbers
 * are chosen, then drawn.
 *
 * - All five are ticks (the line has 20 intervals whatever its scale, so a
 *   plot is drawn on indices 0 to 20 and written in the context's unit), so
 *   each is read off the line, never estimated.
 * - They are strictly increasing, each at least one tick from the next, and
 *   neither end sits on the end of the line.
 * - The median is never at the exact centre of the box, and the whiskers are
 *   never equal, so a plot is not symmetric and the median cannot be had by
 *   halving the box.
 */

/** Every scale has this many intervals between its ends (0-20 by 1, 0-40 by 2, 0-100 by 5). */
export const BOX_PLOT_INTERVALS = 20;

/** A group is three questions about one box plot (ADR 0099 §4). */
export const BOX_PLOT_QUESTIONS_PER_PLOT = 3;

/** The question types a sheet draws from, in rotation order. */
export function boxPlotQuestionTypes(question: BoxPlotQuestionOptions): BoxPlotQuestionOptions[] {
    if (question === BoxPlotQuestionOptions.Mixed) {
        return [
            BoxPlotQuestionOptions.Median,
            BoxPlotQuestionOptions.Quartile,
            BoxPlotQuestionOptions.Range,
            BoxPlotQuestionOptions.InterquartileRange,
        ];
    }
    return [question];
}

/**
 * Question types with exactly one true answer per plot. A sheet pinned to one
 * of them has one question to ask about each plot, so it draws a plot per
 * question and `perPageOptions.ts` offers it its own, lower, counts.
 * `Quartile` has two (the lower and the upper).
 */
export const SINGLE_ANSWER_BOX_PLOT_QUESTIONS: BoxPlotQuestionOptions[] = [
    BoxPlotQuestionOptions.Median,
    BoxPlotQuestionOptions.Range,
    BoxPlotQuestionOptions.InterquartileRange,
];

/** How many distinct questions one plot supports for this sheet, at most three. */
export function questionsPerPlot(question: BoxPlotQuestionOptions): number {
    if (question === BoxPlotQuestionOptions.Mixed) return BOX_PLOT_QUESTIONS_PER_PLOT;
    if (question === BoxPlotQuestionOptions.Quartile) return 2;
    return 1;
}

/**
 * Five tick indices that meet the rules above. Drawn in order, with the two
 * symmetry rules checked by a bounded loop (about one draw in four fails
 * them); the fallback is a plot that meets them.
 */
export function fiveNumberIndices(): [number, number, number, number, number] {
    for (let attempt = 0; attempt < 100; attempt++) {
        const min = getRandomInt(1, 6);
        const max = getRandomInt(min + 8, BOX_PLOT_INTERVALS - 1);
        const q1 = getRandomInt(min + 1, max - 3);
        const median = getRandomInt(q1 + 1, max - 2);
        const q3 = getRandomInt(median + 1, max - 1);
        if (median - q1 !== q3 - median && q1 - min !== max - q3) {
            return [min, q1, median, q3, max];
        }
    }
    return [2, 5, 7, 12, 16];
}

/** A freshly drawn plot: its title, its line and its five numbers. */
function buildPlot(language: PrintedLanguage, usedTitles: Set<string>): BoxPlotSpec {
    const all = CONTEXTS[language];
    // Two plots on one page about the same thing read as a mistake, so a page
    // uses each context once while it has unused ones left.
    const fresh = all.filter((context) => !usedTitles.has(context.title));
    const contexts = fresh.length > 0 ? fresh : all;
    const context = contexts[getRandomInt(0, contexts.length - 1)];
    usedTitles.add(context.title);

    const [min, q1, median, q3, max] = fiveNumberIndices().map((index) => context.from + index * context.step);
    return { title: context.title, from: context.from, to: context.to, step: context.step, min, q1, median, q3, max };
}

/** What a group has already asked about its plot, so the next question is new (ADR 0040 §3). */
interface GroupUsage {
    once: Set<BoxPlotQuestionOptions>;
    quartiles: Set<'lower' | 'upper'>;
}

function tryBuildQuestion(
    type: BoxPlotQuestionOptions,
    spec: BoxPlotSpec,
    used: GroupUsage,
    language: PrintedLanguage
): { prompt: string; answer: string; commit: () => void } | null {
    const t = PROMPTS[language];
    const commitOnce = () => { used.once.add(type); };

    if (SINGLE_ANSWER_BOX_PLOT_QUESTIONS.includes(type) && used.once.has(type)) {
        return null;
    }

    switch (type) {
        case BoxPlotQuestionOptions.Median:
            return { prompt: t.median(), answer: String(spec.median), commit: commitOnce };
        case BoxPlotQuestionOptions.Range:
            return { prompt: t.range(), answer: String(spec.max - spec.min), commit: commitOnce };
        case BoxPlotQuestionOptions.InterquartileRange:
            return { prompt: t.interquartileRange(), answer: String(spec.q3 - spec.q1), commit: commitOnce };
        default: {
            // Quartile: the lower or the upper, whichever this plot has not been asked.
            const available = (['lower', 'upper'] as const).filter((which) => !used.quartiles.has(which));
            if (available.length === 0) return null;
            const which = available[getRandomInt(0, available.length - 1)];
            return {
                prompt: which === 'lower' ? t.lowerQuartile() : t.upperQuartile(),
                answer: String(which === 'lower' ? spec.q1 : spec.q3),
                commit: () => used.quartiles.add(which),
            };
        }
    }
}

/**
 * One plot and up to `want` distinct questions about it, all tagged with the
 * same group so the page draws it once. Comes back short when the allowed
 * types run out of new questions for this plot (a pinned `Median` sheet has one
 * per plot); the caller draws a fresh plot for the rest. `startTypeIndex`
 * carries a Mixed sheet's rotation across groups, for the reason
 * `buildBarGraphGroup` gives. Three consecutive types out of four are three
 * different types.
 */
export function buildBoxPlotGroup(
    question: BoxPlotQuestionOptions,
    want: number,
    groupKey: string,
    startKey: number,
    startTypeIndex: number,
    language: PrintedLanguage,
    usedTitles: Set<string> = new Set()
): { problems: ProblemData[]; nextKey: number; nextTypeIndex: number } {
    const types = boxPlotQuestionTypes(question);
    const spec = buildPlot(language, usedTitles);
    const used: GroupUsage = { once: new Set(), quartiles: new Set() };
    const problems: ProblemData[] = [];
    let key = startKey;
    let typeIndex = startTypeIndex;
    let stalled = 0;

    while (problems.length < want && stalled < types.length) {
        const type = types[typeIndex % types.length];
        typeIndex++;
        const picked = tryBuildQuestion(type, spec, used, language);
        if (!picked) {
            stalled++;
            continue;
        }
        stalled = 0;
        picked.commit();
        problems.push({
            key: key++,
            numbers: [],
            operator: "",
            display: [picked.prompt, picked.answer],
            boxPlotQuestion: type,
            problemGroup: { key: groupKey, format: ProblemFormatOptions.BoxPlots, boxPlot: spec },
        });
    }

    return { problems, nextKey: key, nextTypeIndex: typeIndex % types.length };
}

/** A page-shaped set of box-plot problems, up to three questions to a plot. */
export function generateBoxPlotPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.BoxPlotQuestion ?? BoxPlotQuestionOptions.Mixed;
    const types = boxPlotQuestionTypes(question);
    // Whole plots on a Mixed sheet: a count carried over from another format,
    // such as 4, would leave a plot with a single question (ADR 0099 §4). A
    // sheet pinned to one question type has no such unit, and draws as many
    // plots as its questions need.
    const count = settings.NumberOfProblemsPerPage;
    const perPage = question === BoxPlotQuestionOptions.Mixed
        ? Math.max(BOX_PLOT_QUESTIONS_PER_PLOT, count - (count % BOX_PLOT_QUESTIONS_PER_PLOT))
        : count;
    const perPlot = questionsPerPlot(question);
    const language = printedLanguageOf(settings);
    const pages: ProblemData[][] = [];
    let nextKey = 1;

    for (let page = 0; page < settings.NumberOfPages; page++) {
        const problems: ProblemData[] = [];
        let groupIndex = 0;
        // A Mixed page starts its rotation anywhere, so a page of one plot
        // does not always leave out the same question type.
        let typeIndex = types.length > 1 ? getRandomInt(0, types.length - 1) : 0;
        const usedTitles = new Set<string>();
        // Every group yields at least one question about a fresh plot, so this
        // always makes progress; the guard turns a broken guarantee into a
        // short page rather than a hung tab.
        const guardLimit = perPage * 4 + 10;
        let guard = 0;

        while (problems.length < perPage && guard < guardLimit) {
            guard++;
            const want = Math.min(perPlot, perPage - problems.length);
            const group = buildBoxPlotGroup(
                question,
                want,
                `box-plot:${page}:${groupIndex}`,
                nextKey,
                typeIndex,
                language,
                usedTitles
            );
            if (group.problems.length === 0) {
                break;
            }
            nextKey = group.nextKey;
            typeIndex = group.nextTypeIndex;
            problems.push(...group.problems);
            groupIndex++;
        }

        pages.push(problems);
    }

    return pages.some((page) => page.length > 0) ? pages : null;
}
